import { useEventBulkUnrsvp, type UserHistoryDto, useUserGetUserRsvpsSearch } from "@crowdcoursing/api/openapi";
import { Box, type BoxProps, CircularProgress, Typography } from "@mui/material";
import { useCallback } from "react";

import { useToast } from "~hooks/useToast";
import { RenderStates } from "~ui/atoms/RenderStates/RenderStates";
import { RsvpTable, type RsvpTableProps } from "~ui/organisms/RsvpTable";
import { valueOrFallback } from "~utils/sanitizeDto";

import { UserHistoryRoomsAndEvents } from "./UserHistoryRoomsAndEvents";
import { type UserHistoryStatsProps, UserHistoryVolunteerStats } from "./UserHistoryStats";
import { UserHistoryVolunteerRecords } from "./UserHistoryVolunteers";

const tUserHistoryError = "An error occurred while retrieving user history";
const tNoUserHistoryFound = "No user history found";
const tNoUserIdFound = "No user ID found";
const tLoading = "Loading";
const tNoScheduleFound = "No upcoming schedule found";
const tErrorLoadingSchedule = "Error loading schedule";

function Empty(props: BoxProps) {
  return (
    <Box data-testid="UserHistory-empty" {...props}>
      <Typography sx={{ color: (theme) => theme.palette.text.disabled }}>{tNoUserHistoryFound}</Typography>
    </Box>
  );
}

function Error(props: BoxProps) {
  return (
    <Box data-testid="UserHistory-error" {...props}>
      <Typography sx={{ color: (theme) => theme.palette.text.disabled }}>{tUserHistoryError}</Typography>
    </Box>
  );
}

function Pending(props: BoxProps) {
  return (
    <Box data-testid="UserHistory-pending" {...props}>
      <Typography sx={{ color: (theme) => theme.palette.text.disabled }}>{tLoading}</Typography>
    </Box>
  );
}

export interface UserHistoryBaseProps {
  data?: UserHistoryDto;
  showVolunteerStats?: boolean;
  showUserUpcomingRsvps?: boolean;
  status: Utils.QueryStatus;
  homeTemplate?: boolean;
}
export interface UserHistorySlotProps {
  slotProps?: { container?: BoxProps; stats?: UserHistoryStatsProps["slotProps"] };
}
export type UserHistoryProps = UserHistoryBaseProps & UserHistorySlotProps;

export function UserHistory({
  data,
  showVolunteerStats = true,
  showUserUpcomingRsvps = false,
  status,
  slotProps,
}: UserHistoryProps): JSX.Element {
  const userId = data?.id ?? "";
  const toast = useToast();

  // Fetch RSVPs for the user if showUserUpcomingRsvps is true
  const {
    data: rsvpsData,
    status: rsvpsStatus,
    refetch,
  } = useUserGetUserRsvpsSearch({ pathParams: { userId } }, { enabled: showUserUpcomingRsvps && Boolean(userId) });

  const { mutate: bulkUnrsvp } = useEventBulkUnrsvp();

  const handleRemove = useCallback(
    (rsvpIds: string[]) => {
      const count = rsvpIds.length;
      bulkUnrsvp(
        {
          body: {
            callerUserId: userId,
            eventTimeRsvps: rsvpIds,
          },
        },
        {
          onSuccess: () => {
            refetch();
            toast.success(`Successfully removed ${count} ${count === 1 ? "RSVP" : "RSVPs"}`);
          },
        },
      );
    },
    [bulkUnrsvp, refetch, toast, userId],
  );

  return (
    <RenderStates
      data={data}
      empty={<Empty {...slotProps?.container} />}
      error={<Error {...slotProps?.container} />}
      pending={<Pending {...slotProps?.container} />}
      status={status}
      success={function ({ rooms, events }) {
        return (
          <Box
            data-testid="UserHistory"
            {...slotProps?.container}
            sx={{ display: "grid", gap: "2rem", ...slotProps?.container?.sx }}
          >
            {showVolunteerStats && (
              <>
                <UserHistoryVolunteerStats volunteerStats={data?.volunteerStats ?? {}} slotProps={slotProps?.stats} />
                <UserHistoryVolunteerRecords data={data?.volunteeringRecords ?? []} userId={data?.id ?? ""} />
              </>
            )}
            {showUserUpcomingRsvps && (
              <Box>
                {userId ? (
                  <RenderStates
                    data={rsvpsData}
                    empty={
                      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          {tNoScheduleFound}
                        </Typography>
                      </Box>
                    }
                    error={
                      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", py: 2 }}>
                        <Typography color="error" variant="body2">
                          {tErrorLoadingSchedule}
                        </Typography>
                      </Box>
                    }
                    pending={
                      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    }
                    status={rsvpsStatus}
                    success={(rsvpsData) => {
                      const rsvps: RsvpTableProps["rsvps"] = rsvpsData.map(
                        ({ id, bubbleId, bubbleName, eventId, eventName, meetingTimeDto }) => ({
                          beginMeetingTime: new Date(
                            valueOrFallback(
                              meetingTimeDto,
                              { beginMeetingTime: "" },
                              "(UserHistory) Missing meetingTimeDto",
                            ).beginMeetingTime ?? 0,
                          ),
                          bubbleId: bubbleId ?? undefined,
                          bubbleName: bubbleName ?? undefined,
                          endMeetingTime: new Date(
                            valueOrFallback(
                              meetingTimeDto,
                              { endMeetingTime: "" },
                              "(UserHistory) Missing meetingTimeDto",
                            ).endMeetingTime ?? 0,
                          ),
                          eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(UserHistory) Missing eventId"),
                          eventName: valueOrFallback(
                            eventName,
                            "MISSING_EVENT_NAME",
                            "(UserHistory) Missing eventName",
                          ),
                          rsvpId: valueOrFallback(id, "MISSING_RSVP_ID", "(UserHistory) Missing rsvpId"),
                          rsvpUserId: userId,
                        }),
                      );
                      const handleClickRow: RsvpTableProps["onClickRow"] = () => {};

                      return (
                        <RsvpTable
                          hideHeader={false}
                          onClickRow={handleClickRow}
                          onRemove={handleRemove}
                          rsvps={rsvps}
                        />
                      );
                    }}
                  />
                ) : (
                  <Typography>{tNoUserIdFound}</Typography>
                )}
              </Box>
            )}
            <UserHistoryRoomsAndEvents rooms={rooms ?? []} events={events ?? []} />
          </Box>
        );
      }}
    />
  );
}
